import { useState, useEffect } from 'react';
import { Plus, RefreshCw, Edit, Trash2, Activity, BarChart3, Network, FolderPlus, Download } from 'lucide-react';
import { useTenantStore } from '../stores/tenantStore';
import DataTable, { type Column } from '../components/common/DataTable';
import StatCard from '../components/common/StatCard';
import ConfirmDialog from '../components/common/ConfirmDialog';
import ApplicationModal from '../components/applications/ApplicationModal';
import ApplicationGroupModal from '../components/applications/ApplicationGroupModal';
import { applicationService } from '../services';
import type { ApplicationDefinition, ApplicationGroup } from '../services';
import { toast } from 'react-hot-toast';
import { usePermissions } from '../hooks/usePermissions';


const Applications = () => {
  const { canCreate, canUpdate, canDelete } = usePermissions();
  const [apps, setApps] = useState<ApplicationDefinition[]>([]);
  const [appGroups, setAppGroups] = useState<ApplicationGroup[]>([]);
  const [isLoadingApps, setIsLoadingApps] = useState(false);
  const [isLoadingGroups, setIsLoadingGroups] = useState(false);

  const [page] = useState(1);
  const [pageSize] = useState(100);

  const selectedTenantId = useTenantStore((state) => state.selectedTenantId);

  // Modal states
  const [isAppModalOpen, setIsAppModalOpen] = useState(false);
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [editingApp, setEditingApp] = useState<any>(null);
  const [editingGroup, setEditingGroup] = useState<any>(null);

  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; type: string; id: string; name: string }>(
    { isOpen: false, type: '', id: '', name: '' }
  );

  // Fetch applications
  const fetchApplications = async () => {
    setIsLoadingApps(true);
    try {
      const response = await applicationService.list({
        page,
        pageSize,
        tenantId: selectedTenantId
      });
      setApps([...response.items].sort((a, b) => a.name.localeCompare(b.name)));
    } catch (error) {
      console.error('Failed to fetch applications:', error);
      toast.error('Failed to load applications');
    } finally {
      setIsLoadingApps(false);
    }
  };

  // Fetch application groups
  const fetchApplicationGroups = async () => {
    setIsLoadingGroups(true);
    try {
      const response = await applicationService.listGroups({
        page,
        pageSize,
        tenantId: selectedTenantId
      });
      setAppGroups([...response.items].sort((a, b) => a.name.localeCompare(b.name)));
    } catch (error) {
      console.error('Failed to fetch application groups:', error);
      toast.error('Failed to load application groups');
    } finally {
      setIsLoadingGroups(false);
    }
  };

  useEffect(() => {
    fetchApplications();
    fetchApplicationGroups();
  }, [page, pageSize, selectedTenantId]);

  const [reconciling, setReconciling] = useState(false);

  const handleRefresh = () => {
    fetchApplications();
    fetchApplicationGroups();
  };

  const handleReconcileDefaults = async () => {
    try {
      setReconciling(true);
      const result = await applicationService.reconcileDefaults(selectedTenantId || undefined);
      const total = result.appsAdded + result.groupsAdded + result.membersAdded + result.policiesAdded;
      if (total > 0) {
        toast.success(`Added ${result.appsAdded} apps, ${result.groupsAdded} groups, ${result.membersAdded} memberships, ${result.policiesAdded} policies`);
        fetchApplications();
        fetchApplicationGroups();
      } else {
        toast.success('All defaults are already present');
      }
    } catch (error: any) {
      console.error('Failed to reconcile defaults:', error);
      const msg = error?.response?.data?.error?.message || error?.response?.data?.error || 'Failed to reconcile defaults';
      toast.error(typeof msg === 'string' ? msg : 'Failed to reconcile defaults');
    } finally {
      setReconciling(false);
    }
  };


  // CRUD Handlers
  const handleSaveApp = async (data: any) => {
    try {
      if (editingApp) {
        await applicationService.update(editingApp.id, data);
        toast.success('Application updated successfully');
      } else {
        await applicationService.create(data);
        toast.success('Application created successfully');
      }
      setIsAppModalOpen(false);
      setEditingApp(null);
      fetchApplications();
      fetchApplicationGroups(); // Refresh groups to update member counts if needed
    } catch (error: any) {
      console.error('Failed to save application:', error);
      const msg2 = error?.response?.data?.error?.message || error?.response?.data?.error || 'Failed to save application';
      toast.error(typeof msg2 === 'string' ? msg2 : 'Failed to save application');
    }
  };

  const handleSaveAppGroup = async (data: any) => {
    try {
      if (editingGroup) {
        // App group update not fully implemented in frontend service yet according to previous code, 
        // but backend supports it. Assuming applicationService.updateGroup exists or using createGroup logic?
        // Checking previous code: "Group update not yet implemented" toast.
        // We should try to implement or keep the warning. 
        // For now, I'll keep the toast if service doesn't have updateGroup, but I didn't check service fully.
        // I will assume it's like before for safety, but check if I can improve.
        toast('Group update not yet implemented', { icon: '⚠️' });
      } else {
        await applicationService.createGroup(data);
        toast.success('Application group created successfully');
      }
      setIsGroupModalOpen(false);
      setEditingGroup(null);
      fetchApplicationGroups();
    } catch (error: any) {
      console.error('Failed to save application group:', error);
      const msg3 = error?.response?.data?.error?.message || error?.response?.data?.error || 'Failed to save application group';
      toast.error(typeof msg3 === 'string' ? msg3 : 'Failed to save application group');
    }
  };

  const handleDeleteApp = async (app: ApplicationDefinition) => {
    setDeleteConfirm({ isOpen: true, type: 'Application', id: app.id, name: app.name });
  };

  const handleDeleteGroup = async (group: ApplicationGroup) => {
    setDeleteConfirm({ isOpen: true, type: 'Group', id: group.id, name: group.name });
  };

  const handleConfirmDelete = async () => {
    const { type, id } = deleteConfirm;
    try {
      if (type === 'Application') {
        await applicationService.delete(id);
        toast.success('Application deleted successfully');
        fetchApplications();
        fetchApplicationGroups(); // Refresh groups in case app was a member
      } else if (type === 'Group') {
        toast('Group deletion not yet implemented', { icon: '⚠️' });
      }
    } catch (error: any) {
      console.error('Failed to delete:', error);
      const msg4 = error?.response?.data?.error?.message || error?.response?.data?.error || 'Failed to delete';
      toast.error(typeof msg4 === 'string' ? msg4 : 'Failed to delete');
    } finally {
      setDeleteConfirm({ isOpen: false, type: '', id: '', name: '' });
    }
  };

  // Columns
  const appGroupColumns: Column<any>[] = [
    {
      key: 'name',
      label: 'Group Name',
      sortable: true,
      render: (row) => (
        <button
          onClick={() => { setEditingGroup(row); setIsGroupModalOpen(true); }}
          className="flex items-center space-x-2 text-left hover:text-primary-600 transition-colors"
        >
          <Network className="w-4 h-4 text-primary-600" />
          <span className="font-medium">{row.name}</span>
        </button>
      ),
    },
    { key: 'description', label: 'Description' },
    {
      key: 'memberCount',
      label: 'Members',
      render: (row) => <span className="badge badge-gray">{row.memberCount || 0} Apps</span>
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (row) => (
        <div className="flex items-center space-x-2">
          {canUpdate && (
            <button onClick={() => { setEditingGroup(row); setIsGroupModalOpen(true); }} className="p-1 hover:bg-gray-100 rounded text-primary-600">
              <Edit className="w-4 h-4" />
            </button>
          )}
          {canDelete && (
            <button onClick={() => handleDeleteGroup(row)} className="p-1 hover:bg-gray-100 rounded text-red-600">
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      ),
    },
  ];

  const appColumns: Column<ApplicationDefinition>[] = [
    {
      key: 'name',
      label: 'Application',
      sortable: true,
      render: (row) => (
        <button
          onClick={() => { setEditingApp(row); setIsAppModalOpen(true); }}
          className="flex items-center space-x-2 text-left hover:text-primary-600 transition-colors"
        >
          <Activity className="w-4 h-4 text-primary-600" />
          <span className="font-medium">{row.name}</span>
        </button>
      ),
    },
    {
      key: 'category',
      label: 'Category',
      sortable: true,
      render: (row) => row.category ? row.category.replace('_', ' ').toUpperCase() : '-'
    },
    { key: 'protocol', label: 'Protocol', sortable: true, render: (row) => row.protocol?.toUpperCase() || '-' },
    { key: 'destination_ports', label: 'Ports', render: (row) => <span className="font-mono text-xs">{row.destination_ports || '-'}</span> },
    {
      key: 'dscp_values',
      label: 'DSCP',
      sortable: true,
      render: (row) => {
        if (!row.dscp_values) return <span className="text-gray-400">-</span>;
        const val = parseInt(row.dscp_values);
        const phbMap: Record<number, { label: string; color: string }> = {
          0: { label: 'BE', color: 'bg-gray-100 text-gray-600' },
          8: { label: 'CS1', color: 'bg-gray-100 text-gray-600' },
          18: { label: 'AF21', color: 'bg-blue-100 text-blue-700' },
          34: { label: 'AF41', color: 'bg-orange-100 text-orange-700' },
          46: { label: 'EF', color: 'bg-red-100 text-red-700' },
          48: { label: 'CS6', color: 'bg-purple-100 text-purple-700' },
        };
        const info = phbMap[val] || { label: `DSCP ${val}`, color: 'bg-gray-100 text-gray-700' };
        return (
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium font-mono ${info.color}`}>
            {info.label} ({val})
          </span>
        );
      }
    },
    {
      key: 'detection_method',
      label: 'Detection',
      sortable: true,
      render: (row) => {
        if (!row.detection_method || row.detection_method === 'port') {
          return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">Port</span>;
        }
        const methodColors: Record<string, string> = {
          domain: 'bg-blue-100 text-blue-700',
          mixed: 'bg-purple-100 text-purple-700',
          ip_range: 'bg-green-100 text-green-700',
          dpi: 'bg-amber-100 text-amber-700',
        };
        return (
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${methodColors[row.detection_method] || 'bg-gray-100 text-gray-600'}`}>
            {row.detection_method === 'mixed' ? 'Domain+Port' : row.detection_method?.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
          </span>
        );
      }
    },
    {
      key: 'business_criticality',
      label: 'Criticality',
      sortable: true,
      render: (row) => (
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${row.business_criticality === 'critical' ? 'bg-red-100 text-red-700' :
          row.business_criticality === 'high' ? 'bg-orange-100 text-orange-700' :
            row.business_criticality === 'medium' ? 'bg-yellow-100 text-yellow-700' :
              'bg-blue-100 text-blue-700'
          }`}>
          {row.business_criticality || 'low'}
        </span>
      )
    },
    {
      key: 'enabled',
      label: 'Status',
      sortable: true,
      render: (row) => (
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${row.enabled === 1 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
          }`}>
          {row.enabled === 1 ? 'Enabled' : 'Disabled'}
        </span>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (row) => (
        <div className="flex items-center space-x-2">
          {canUpdate && (
            <button onClick={() => { setEditingApp(row); setIsAppModalOpen(true); }} className="p-1 hover:bg-gray-100 rounded text-primary-600">
              <Edit className="w-4 h-4" />
            </button>
          )}
          {canDelete && (
            <button onClick={() => handleDeleteApp(row)} className="p-1 hover:bg-gray-100 rounded text-red-600">
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Applications</h1>
          <p className="text-gray-600 mt-1">Manage mobile VPN clients, groups, and application visibility</p>
        </div>
        <div className="flex items-center space-x-3">
          {canCreate && (
            <button
              onClick={handleReconcileDefaults}
              disabled={reconciling}
              className="btn-secondary inline-flex items-center space-x-2 disabled:opacity-50"
              title="Add any missing default applications, groups, and policies"
            >
              {reconciling ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              <span>{reconciling ? 'Updating...' : 'Update Defaults'}</span>
            </button>
          )}
          <button onClick={handleRefresh} className="btn-secondary inline-flex items-center space-x-2">
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard
          icon={Activity}
          title="Total Apps"
          value={isLoadingApps ? '...' : apps.length}
          color="primary"
        />
        <StatCard
          icon={Network}
          title="Enabled Apps"
          value={isLoadingApps ? '...' : apps.filter(a => a.enabled === 1).length}
          color="success"
        />
        <StatCard
          icon={Activity}
          title="Critical Apps"
          value={isLoadingApps ? '...' : apps.filter(a => a.business_criticality === 'critical').length}
          color="warning"
        />
        <StatCard
          icon={BarChart3}
          title="Application Groups"
          value={isLoadingGroups ? '...' : appGroups.length}
          color="info"
        />
      </div>

      {/* App Groups Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-bold text-gray-900 flex items-center">
            <FolderPlus className="w-5 h-5 mr-2 text-primary-600" />
            Application Groups
          </h3>
          {canCreate && (
            <button
              onClick={() => { setEditingGroup(null); setIsGroupModalOpen(true); }}
              className="btn-primary flex items-center space-x-2 py-1.5 text-sm"
            >
              <Plus className="w-4 h-4" />
              <span>Add Group</span>
            </button>
          )}
        </div>
        <DataTable columns={appGroupColumns} data={appGroups} searchPlaceholder="Search groups..." />
      </div>

      {/* Applications Section */}
      <div className="space-y-4 pt-4 border-t border-gray-100">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-bold text-gray-900 flex items-center">
            <Activity className="w-5 h-5 mr-2 text-primary-600" />
            Applications
          </h3>
          {canCreate && (
            <button
              onClick={() => { setEditingApp(null); setIsAppModalOpen(true); }}
              className="btn-primary flex items-center space-x-2 py-1.5 text-sm"
            >
              <Plus className="w-4 h-4" />
              <span>Add Application</span>
            </button>
          )}
        </div>
        <DataTable columns={appColumns} data={apps} searchPlaceholder="Search applications..." />
      </div>

      {/* Modals */}
      <ApplicationModal
        isOpen={isAppModalOpen}
        onClose={() => setIsAppModalOpen(false)}
        onSave={handleSaveApp}
        application={editingApp}
        mode={editingApp ? 'edit' : 'add'}
        appGroups={appGroups} // Pass groups for selection
      />
      <ApplicationGroupModal
        isOpen={isGroupModalOpen}
        onClose={() => setIsGroupModalOpen(false)}
        onSave={handleSaveAppGroup}
        group={editingGroup}
        mode={editingGroup ? 'edit' : 'add'}
      />

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, type: '', id: '', name: '' })}
        onConfirm={handleConfirmDelete}
        title={`Delete ${deleteConfirm.type}`}
        message={`Are you sure you want to delete "${deleteConfirm.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
};

export default Applications;
